import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Auth } from 'aws-amplify';
import { QRCodeSVG } from 'qrcode.react';

interface Props {
  onClose: () => void;
}

type Status = 'loading' | 'off' | 'enrolling' | 'on';

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const card: React.CSSProperties = {
  background: '#fff', borderRadius: 16, padding: '2rem', minWidth: 360, maxWidth: 440,
  boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
};

/**
 * Self-contained two-factor (TOTP) management for the signed-in Cognito (device)
 * user. Used both from the user profile popup and the post-login nudge. Enrols
 * while signed in (so it works with the pool on Optional), and can disable while
 * MFA is not yet enforced.
 */
const MfaSetupModal: React.FC<Props> = ({ onClose }) => {
  const [status, setStatus] = useState<Status>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [code, setCode] = useState('');
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await Auth.currentAuthenticatedUser();
        const pref = await Auth.getPreferredMFA(u, { bypassCache: true });
        if (!cancelled) setStatus(pref === 'SOFTWARE_TOKEN_MFA' ? 'on' : 'off');
      } catch {
        if (!cancelled) { setStatus('off'); setError('Could not load your two-factor status.'); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Focus the code field when the enrolment step appears.
  useEffect(() => {
    if (status === 'enrolling') {
      const t = setTimeout(() => codeInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [status]);

  const beginEnrol = async () => {
    setError(null); setBusy(true);
    try {
      const u = await Auth.currentAuthenticatedUser();
      const secret = await Auth.setupTOTP(u);
      const issuer = 'Rustle It';
      const account = (typeof u.getUsername === 'function' ? u.getUsername() : 'user') || 'user';
      setTotpSecret(secret);
      setTotpUri(`otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(account)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`);
      setCode('');
      setStatus('enrolling');
    } catch {
      setError('Could not start setup. Please try again.');
    } finally { setBusy(false); }
  };

  const confirmEnrol = async () => {
    setError(null); setBusy(true);
    try {
      const u = await Auth.currentAuthenticatedUser();
      await Auth.verifyTotpToken(u, code.replace(/\D/g, ''));
      await Auth.setPreferredMFA(u, 'TOTP');
      setStatus('on');
    } catch {
      setError('That code didn’t match. Enter the current 6-digit code from your authenticator app.');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setError(null); setBusy(true);
    try {
      const u = await Auth.currentAuthenticatedUser();
      await Auth.setPreferredMFA(u, 'NOMFA');
      setStatus('off');
    } catch {
      setError('Could not turn off two-factor authentication.');
    } finally { setBusy(false); }
  };

  // Render via a portal to document.body: the Account panel uses a CSS
  // transform, which would otherwise become the containing block for this
  // position:fixed overlay and trap it inside the panel.
  return createPortal(
    <div style={overlay} onClick={busy ? undefined : onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <h5 style={{ marginBottom: '0.5rem', fontWeight: 700 }}>Two-factor authentication</h5>

        {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>{error}</div>}

        {status === 'loading' && <p style={{ fontSize: '0.9rem' }}>Loading…</p>}

        {status === 'off' && (
          <>
            <p style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.45 }}>
              Add a second layer of security using an authenticator app
              (Google Authenticator, Microsoft Authenticator or Authy)
              <strong> on your own phone</strong>. Status: <strong>Off</strong>.
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Close</button>
              <button type="button" className="btn btn-primary" onClick={beginEnrol} disabled={busy}>
                {busy ? 'Please wait…' : 'Set up'}
              </button>
            </div>
          </>
        )}

        {status === 'enrolling' && (
          <form onSubmit={e => { e.preventDefault(); confirmEnrol(); }}>
            <p style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.45 }}>
              Scan this code in your authenticator app <strong>on your own phone</strong>,
              then enter the 6-digit code it shows.
            </p>
            <div className="d-flex justify-content-center mb-3">
              {totpUri && <QRCodeSVG value={totpUri} size={170} />}
            </div>
            {totpSecret && (
              <p style={{ fontSize: '0.78rem' }} className="mb-2">
                Can’t scan? Enter this key manually:<br />
                <code style={{ wordBreak: 'break-all' }}>{totpSecret}</code>
              </p>
            )}
            <input ref={codeInputRef} className="form-control mb-3" inputMode="numeric" autoComplete="one-time-code"
              placeholder="6-digit code" value={code} disabled={busy}
              onChange={e => setCode(e.target.value)} />
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={busy || code.trim().length < 6}>
                {busy ? 'Verifying…' : 'Verify & turn on'}
              </button>
            </div>
          </form>
        )}

        {status === 'on' && (
          <>
            <p style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.45 }}>
              Two-factor authentication is <strong>On</strong>. You'll be asked for a code from your
              authenticator app when you sign in on a new device.
            </p>
            <div className="d-flex justify-content-between gap-2 mt-3">
              <button type="button" className="btn btn-outline-danger" onClick={disable} disabled={busy}>
                {busy ? 'Please wait…' : 'Turn off'}
              </button>
              <button type="button" className="btn btn-primary" onClick={onClose} disabled={busy}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MfaSetupModal;
